import PopupManager from "../../Common/Popups/PopupManager";
import BaseAgent, { generateWorkerURL } from "../Bases/BaseAgent";
import { HARDWARE_CONNECT_EVENT } from "./HardwareConnectEvents";
import { type HARDWARE_CONNECT_BROADCAST_EVENTS } from "./HardwareConnectEventTypes";

declare global {
    interface DocumentEventMap {
        [HARDWARE_CONNECT_EVENT]: CustomEvent;
    }
}

export type HardwareConnectBroadcastListener = (message: { event: string; data: unknown; }) => void;

export default class HardwareConnectAgent extends BaseAgent {
    protected broadcastListeners = new Map<HARDWARE_CONNECT_BROADCAST_EVENTS, Set<HardwareConnectBroadcastListener>>();

    constructor() {
        super(generateWorkerURL("hcworker"), "Hardware Connect Agent");

        // Bind handlers
        this.handleShopfrontEvents = this.handleShopfrontEvents.bind(this);
        this.handleMessage = this.handleMessage.bind(this);

        // Start the worker
        this.startWorker();

        // Listen for hardware connect events
        if(typeof document !== "undefined") {
            document.addEventListener(HARDWARE_CONNECT_EVENT, this.handleShopfrontEvents);
        }
    }

    protected handleShopfrontEvents(e: CustomEvent): void {
        if(!e.detail.type) {
            console.error("Unknown event sent to HardwareConnectAgent", e);

            return;
        }

        this.sendMessage(e.detail.type, e.detail.data, e.detail.success);
    }

    public sendMessage(event: string, message: any, success: ((error?: any) => void) | null = null): void {
        if(success) {
            if(typeof success !== "function") {
                success = (error): void => {
                    if(error) {
                        PopupManager.warning("Hardware Connect not connected, try restarting Hardware Connect");
                    }
                };
            }
        }

        super.sendMessage(event, message, success);
    }

    public addBroadcastListener(
        event: HARDWARE_CONNECT_BROADCAST_EVENTS,
        callback: HardwareConnectBroadcastListener
    ): void {
        let listeners = this.broadcastListeners.get(event);

        if(!listeners) {
            listeners = new Set();
            this.broadcastListeners.set(event, listeners);
        }

        listeners.add(callback);
    }

    public removeBroadcastListener(
        event: HARDWARE_CONNECT_BROADCAST_EVENTS,
        callback: HardwareConnectBroadcastListener
    ): void {
        this.broadcastListeners.get(event)?.delete(callback);
    }

    protected handleMessage(data: any): void {
        if(!data.type) {
            console.error("Unknown data passed to HardwareConnectAgent", data);

            return;
        }

        const handlers = this.broadcastListeners.get(data.type);

        if(!handlers) {
            return;
        }

        for(const callback of handlers) {
            callback(data.message);
        }
    }
}

